import clsx from 'clsx';

import type { RevisionStatus } from '@cadstrom/cadstrom-shared';

/**
 * Color styling for revision status (Tailwind CSS classes)
 */
export interface StatusColor {
	dot: string;
	text: string;
}

/**
 * Display labels for revision statuses
 */
export const revisionStatusLabels: Record<RevisionStatus, string> = {
	pending: 'Pending',
	complete: 'Complete',
	processing: 'Processing',
	validating: 'Validating',
	bom_awaiting_review: 'Awaiting BOM review',
	processing_bom: 'Processing BOM',
	ready_to_analyze: 'Ready to analyze',
	processing_design: 'Analyzing',
	analysis_complete: 'Preparing results',
	error: 'Error',
	abandoned: 'Abandoned',
	timed_out: 'Timed out',
	credit_required: 'Design review credit required',
	unknown: 'Unknown',
};

/**
 * Color classes for revision statuses (Tailwind CSS).
 *
 * Hue carries meaning rather than identity: amber where the pipeline has
 * stopped and is waiting on the customer, including for credit to be bought,
 * blue where work is queued or running,
 * green for a finished revision, red for a failed one, gray for an inert one,
 * including one that timed out waiting for input.
 */
export const revisionStatusColors: Record<RevisionStatus, StatusColor> = {
	pending: {
		dot: clsx('bg-amber-9'),
		text: clsx('text-amber-11'),
	},
	complete: {
		dot: clsx('bg-green-9'),
		text: clsx('text-green-11'),
	},
	processing: {
		dot: clsx('bg-blue-9'),
		text: clsx('text-blue-11'),
	},
	validating: {
		dot: clsx('bg-blue-9'),
		text: clsx('text-blue-11'),
	},
	bom_awaiting_review: {
		dot: clsx('bg-amber-9'),
		text: clsx('text-amber-11'),
	},
	processing_bom: {
		dot: clsx('bg-blue-9'),
		text: clsx('text-blue-11'),
	},
	ready_to_analyze: {
		dot: clsx('bg-amber-9'),
		text: clsx('text-amber-11'),
	},
	processing_design: {
		dot: clsx('bg-blue-9'),
		text: clsx('text-blue-11'),
	},
	analysis_complete: {
		dot: clsx('bg-blue-9'),
		text: clsx('text-blue-11'),
	},
	error: {
		dot: clsx('bg-red-9'),
		text: clsx('text-red-11'),
	},
	abandoned: {
		dot: 'bg-gray-9',
		text: 'text-gray-11',
	},
	timed_out: {
		dot: clsx('bg-gray-9'),
		text: clsx('text-gray-11'),
	},
	credit_required: {
		dot: clsx('bg-amber-9'),
		text: clsx('text-amber-11'),
	},
	unknown: {
		dot: clsx('bg-gray-9'),
		text: clsx('text-gray-11'),
	},
};

/**
 * Get the display label for a revision status
 */
export function getRevisionStatusLabel(status: RevisionStatus): string {
	return revisionStatusLabels[status];
}

/**
 * Get the color classes for a revision status.
 * Returns Tailwind CSS classes for the dot and text.
 */
export function getRevisionStatusColor(status: RevisionStatus): StatusColor {
	return revisionStatusColors[status];
}
